// Kartlag «Kritiske objekter» (J-5): militære, energi- og transport-/logistikk-
// objekter for alle HV-distrikter, med MGRS-koordinater. Toggle-bart av/på med
// egne undergrupper (MIL/EN/TR) — AV ved oppstart (kartet er helten,
// UX_DESIGNSYSTEM §2), men laget slås PÅ (ikke av) når operatøren ber om det, og
// zoomer til nærmeste objekter hvis utsnittet er tomt. Grunndata er
// generert fra vale/data/kritiske-objekter-j5.md (@skade/contracts
// KRITISKE_GYLDIGE), men KAN redigeres i felt: flytt objekt + endre navn/kategori.
// Redigeringer lagres lokalt (localStorage) og legges oppå grunndata.
import type { Map as MlMap, MapGeoJSONFeature } from 'maplibre-gl';
import ms from 'milsymbol';
import {
  featureCollection, KRITISKE_GYLDIGE, type GeoFeature, type KritiskKategori, type KritiskObjekt,
} from '@skade/contracts';

// APP-6D installasjonssymboler (symbolsett 20 = landinstallasjoner, venn).
// Tegnes monokromt per kategori (monoColor) — ekte militærsymbol, ikke prikk,
// uten å misbruke tilhørighetsfargen (blå=venn) til kategorikoding.
// Entitetskoden er kategori-RIKTIG (verifisert mot milsymbol 3.0.4 sitt
// landinstallation-register) slik at operatøren SER hva objektet er:
//   120800 = militær infrastruktur · 120500 = elektrisk kraft · 121300 = transport
const SIDC = (entity: string) => `1003200000${entity}0000`;

/** Farge + etikett + APP-6-symbol per kategori. */
const KAT: Record<KritiskKategori, { color: string; label: string; layer: string; sidc: string }> = {
  MIL: { color: '#d64545', label: 'Militære', layer: 'ko-mil', sidc: SIDC('120800') },
  EN: { color: '#e0a92e', label: 'Energi', layer: 'ko-en', sidc: SIDC('120500') },
  TR: { color: '#3fa7c4', label: 'Transport/log', layer: 'ko-tr', sidc: SIDC('121300') },
};

const OVERRIDE_KEY = 'skade.ko.overrides';

/** Felt en operatør kan overstyre per objekt (resten arves fra grunndata). */
export interface KritiskOverride {
  name?: string;
  why?: string;
  kat?: KritiskKategori;
  lat?: number;
  lon?: number;
}

export class KritiskeLag {
  private on: Record<KritiskKategori, boolean> = { MIL: false, EN: false, TR: false };
  private ready = false;
  private overrides: Record<string, KritiskOverride> = {};
  private editing: string | null = null;
  /** Kalles når et objekt endres (til deling/broadcast av hovedappen). */
  onChange?: (obj: KritiskObjekt) => void;
  /** Kalles hver gang av/på-tilstanden endres — dock-knapp + tegnforklaring
   *  speiler tilstanden i stedet for å gjette. */
  onStateChange?: () => void;

  constructor(private map: MlMap) {
    try { this.overrides = JSON.parse(localStorage.getItem(OVERRIDE_KEY) ?? '{}') || {}; } catch { this.overrides = {}; }
    if (map.loaded()) this.init();
    else map.on('load', () => this.init());
  }

  /** Slå sammen grunndata + evt. overstyring for ett objekt. */
  private merged(o: KritiskObjekt): KritiskObjekt {
    const ov = this.overrides[o.id];
    if (!ov) return o;
    const kat = ov.kat ?? o.kat;
    return {
      ...o,
      name: ov.name ?? o.name,
      why: ov.why ?? o.why,
      kat,
      cats: ov.kat ? [ov.kat, ...o.cats.filter((c) => c !== ov.kat)] : o.cats,
      lat: ov.lat ?? o.lat,
      lon: ov.lon ?? o.lon,
    };
  }

  private allMerged(): KritiskObjekt[] {
    return KRITISKE_GYLDIGE.map((o) => this.merged(o));
  }

  private toFeatures(): GeoFeature[] {
    return this.allMerged().map((o) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [o.lon, o.lat] },
      properties: {
        id: o.id, name: o.name, kat: o.kat, why: o.why,
        district: o.districtName, mgrs: o.mgrs,
        edited: !!this.overrides[o.id],
      },
    }));
  }

  /** Registrer APP-6 installasjonssymbol (monokromt) per kategori som kart-bilde. */
  private registerSymbols() {
    for (const kat of Object.keys(KAT) as KritiskKategori[]) {
      const key = `ko-sym-${kat}`;
      if (this.map.hasImage(key)) continue;
      try {
        const sym = new ms.Symbol(KAT[kat].sidc, {
          size: 30, standard: 'APP6', monoColor: KAT[kat].color, fill: false,
          outlineWidth: 3, outlineColor: '#0a0c0a',
        });
        const canvas = sym.asCanvas() as HTMLCanvasElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        const { width, height } = canvas;
        const data = ctx.getImageData(0, 0, width, height);
        this.map.addImage(key, { width, height, data: data.data }, { pixelRatio: 2 });
      } catch { /* milsymbol utilgjengelig — hopper over (faller til tekst) */ }
    }
  }

  private init() {
    if (this.ready || this.map.getSource('kritiske')) return;
    this.map.addSource('kritiske', { type: 'geojson', data: featureCollection(this.toFeatures()) as never });
    this.registerSymbols();

    for (const kat of Object.keys(KAT) as KritiskKategori[]) {
      const { color, layer } = KAT[kat];
      // Ett symbol-lag per kategori: APP-6 installasjonsikon + navneetikett.
      this.map.addLayer({
        id: layer, type: 'symbol', source: 'kritiske',
        filter: ['==', ['get', 'kat'], kat],
        layout: {
          visibility: 'none',
          'icon-image': `ko-sym-${kat}`,
          'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 0.4, 10, 0.6, 14, 0.8],
          'icon-allow-overlap': true,
          'text-field': ['get', 'name'], 'text-font': ['Open Sans Regular'],
          'text-size': 10, 'text-offset': [0, 1.5], 'text-anchor': 'top',
          'text-optional': true, 'text-max-width': 10,
        },
        paint: {
          'text-color': color, 'text-halo-color': '#0a0c0a', 'text-halo-width': 1.4,
        },
      });
      // Diskret ring bak redigerte objekter (viser at de er flyttet/endret).
      this.map.addLayer({
        id: `${layer}-edited`, type: 'circle', source: 'kritiske',
        filter: ['all', ['==', ['get', 'kat'], kat], ['==', ['get', 'edited'], true]],
        layout: { visibility: 'none' },
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 7, 14, 15],
          'circle-color': 'rgba(0,0,0,0)', 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1.5, 'circle-stroke-opacity': 0.7,
        },
      });
    }
    this.ready = true;
  }

  /** Oppdater kartkilden etter en endring. */
  private refresh() {
    const src = this.map.getSource('kritiske') as { setData?: (d: unknown) => void } | undefined;
    src?.setData?.(featureCollection(this.toFeatures()));
  }

  /** Slå én kategori av/på (MIL/EN/TR). */
  toggle(kat: KritiskKategori): boolean {
    return this.setOn(kat, !this.on[kat]);
  }

  /** Sett én kategori eksplisitt av/på (idempotent). Returnerer ny tilstand. */
  setOn(kat: KritiskKategori, on: boolean): boolean {
    this.init();
    this.on[kat] = on;
    const v = on ? 'visible' : 'none';
    this.map.setLayoutProperty(KAT[kat].layer, 'visibility', v);
    this.map.setLayoutProperty(`${KAT[kat].layer}-edited`, 'visibility', v);
    this.onStateChange?.();
    return on;
  }

  toggleAll(): boolean {
    const anyOn = Object.values(this.on).some(Boolean);
    const target = !anyOn;
    for (const kat of Object.keys(KAT) as KritiskKategori[]) {
      if (this.on[kat] !== target) this.setOn(kat, target);
    }
    return target;
  }

  /** Slå PÅ alle kategorier (idempotent) — brukes når operatøren ber om å se
   *  laget: å «toggle» et delvis påslått lag AV er den klassiske
   *  «jeg-ser-ingenting»-fellen. Returnerer true (laget er på). */
  enableAll(): boolean {
    for (const kat of Object.keys(KAT) as KritiskKategori[]) if (!this.on[kat]) this.setOn(kat, true);
    return true;
  }

  isOn(kat: KritiskKategori): boolean { return this.on[kat]; }
  /** Er minst én kategori på? */
  anyOn(): boolean { return Object.values(this.on).some(Boolean); }

  // ── Tellere (tegnforklaring + dock-knapp) ─────────────────────────────────
  /** Antall objekter per kategori i grunndata + overstyringer. */
  counts(): Record<KritiskKategori, number> {
    const c: Record<KritiskKategori, number> = { MIL: 0, EN: 0, TR: 0 };
    for (const o of this.allMerged()) c[o.kat] += 1;
    return c;
  }

  /** Antall objekter som faktisk er SYNLIGE nå (kun påslåtte kategorier). */
  visibleCount(): number {
    const c = this.counts();
    return (Object.keys(KAT) as KritiskKategori[]).reduce((n, k) => n + (this.on[k] ? c[k] : 0), 0);
  }

  /** Synlige objekter innenfor gjeldende kartutsnitt (0 ⇒ ingenting å se her). */
  inViewCount(): number {
    const b = this.bounds();
    if (!b) return 0;
    return this.enabled().filter((o) => o.lon >= b.west && o.lon <= b.east && o.lat >= b.south && o.lat <= b.north).length;
  }

  /** Objekter i påslåtte kategorier. */
  private enabled(): KritiskObjekt[] {
    return this.allMerged().filter((o) => this.on[o.kat]);
  }

  private bounds(): { west: number; south: number; east: number; north: number } | null {
    try {
      const b = this.map.getBounds?.();
      if (!b) return null;
      return { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() };
    } catch { return null; }
  }

  /**
   * Zoom til de `n` nærmeste synlige objektene fra kartsenter. Kalles når laget
   * er på men ingenting er i utsnittet — så operatøren ser objekter i stedet for
   * et tomt kart. Returnerer antall objekter rammen ble lagt rundt (0 = ingen).
   */
  fitToNearest(n = 8): number {
    const objs = this.enabled();
    if (!objs.length) return 0;
    let cx = 10.7, cy = 59.9; // Oslofjorden som fallback om kartet ikke svarer
    try { const c = this.map.getCenter?.(); if (c) { cx = c.lng; cy = c.lat; } } catch { /* bruk fallback */ }
    const near = objs
      .map((o) => ({ o, d: (o.lon - cx) ** 2 + ((o.lat - cy) * 2) ** 2 }))
      .sort((a, b) => a.d - b.d)
      .slice(0, Math.max(1, n))
      .map((x) => x.o);
    const west = Math.min(...near.map((o) => o.lon));
    const east = Math.max(...near.map((o) => o.lon));
    const south = Math.min(...near.map((o) => o.lat));
    const north = Math.max(...near.map((o) => o.lat));
    // Litt luft rundt, og en minsteutstrekning så ett enkelt punkt ikke gir zoom 22.
    const padLon = Math.max(0.06, (east - west) * 0.15);
    const padLat = Math.max(0.03, (north - south) * 0.15);
    try {
      this.map.fitBounds?.(
        [[west - padLon, south - padLat], [east + padLon, north + padLat]] as never,
        { padding: 80, maxZoom: 11, duration: 900 } as never,
      );
    } catch { /* kart uten fitBounds (test) — tellingen er fortsatt gyldig */ }
    return near.length;
  }

  /**
   * PNG-data-URL av kategoriens APP-6 installasjonssymbol — tegnforklaringen
   * viser NØYAKTIG samme symbol som kartet, ikke en farget prikk.
   * Tom streng når canvas/milsymbol ikke er tilgjengelig (node/test).
   */
  symbolDataUrl(kat: KritiskKategori): string {
    try {
      const sym = new ms.Symbol(KAT[kat].sidc, {
        size: 26, standard: 'APP6', monoColor: KAT[kat].color, fill: false,
        outlineWidth: 2, outlineColor: '#0a0c0a',
      });
      const canvas = sym.asCanvas() as HTMLCanvasElement;
      return typeof canvas.toDataURL === 'function' ? canvas.toDataURL('image/png') : '';
    } catch { return ''; }
  }

  /** Klikk-treff: returner objektet under peker (for popup/detaljer), om noe. */
  hitAt(point: { x: number; y: number }): MapGeoJSONFeature | null {
    const layers = (Object.keys(KAT) as KritiskKategori[])
      .map((k) => KAT[k].layer)
      .filter((l) => this.map.getLayer(l));
    if (!layers.length) return null;
    const hits = this.map.queryRenderedFeatures([point.x, point.y] as never, { layers });
    return hits[0] ?? null;
  }

  // ── Redigering ────────────────────────────────────────────────────────────
  byId(id: string): KritiskObjekt | null {
    const base = KRITISKE_GYLDIGE.find((o) => o.id === id);
    return base ? this.merged(base) : null;
  }

  /** Start flytting: neste kartklikk flytter objektet dit. */
  startMove(id: string) { this.editing = id; }
  isMoving(): boolean { return this.editing != null; }
  cancelMove() { this.editing = null; }

  /** Flytt objektet som redigeres til et koordinat. Returnerer objektet. */
  relocateTo(lon: number, lat: number): KritiskObjekt | null {
    if (!this.editing) return null;
    const id = this.editing;
    this.setOverride(id, { lon: Number(lon.toFixed(5)), lat: Number(lat.toFixed(5)) });
    this.editing = null;
    return this.byId(id);
  }

  /** Endre navn/kategori/hvorfor. Returnerer oppdatert objekt. */
  editMeta(id: string, patch: Pick<KritiskOverride, 'name' | 'why' | 'kat'>): KritiskObjekt | null {
    this.setOverride(id, patch);
    return this.byId(id);
  }

  private setOverride(id: string, patch: KritiskOverride) {
    this.overrides[id] = { ...this.overrides[id], ...patch };
    try { localStorage.setItem(OVERRIDE_KEY, JSON.stringify(this.overrides)); } catch { /* full */ }
    this.refresh();
    const obj = this.byId(id);
    if (obj) this.onChange?.(obj);
  }
}

export const KRITISK_KAT = KAT;
